import { ENV } from '../../config/runtimeEnv';
import {
	Protocol,
	ProtocolFilter,
	PaginatedProtocolResponse,
	ProtocolStatistics,
	CompatibleProtocolsRequest,
	CloneProtocolRequest
} from "../../types/lab/ProtocolTypes";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_LABIQ_SERVER;

// Get all protocols with filtering and pagination
export const fetchProtocols = async (
	filters: ProtocolFilter = {},
): Promise<PaginatedProtocolResponse> => {
	const params = new URLSearchParams();

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== '') {
			params.append(key, value.toString());
		}
	});

	const response = await http.get(`${API_BASE_URL}/protocols?${params.toString()}`);

	return response.data;
};

// Get a specific protocol by ID
export const fetchProtocol = async (id: number): Promise<Protocol> => {
	const response = await http.get(`${API_BASE_URL}/protocols/${id}`);

	return response.data;
};

// Create a new protocol
export const createProtocol = async (protocolData: Partial<Protocol>): Promise<Protocol> => {
	const response = await http.post(
		`${API_BASE_URL}/protocols`,
		protocolData,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data.protocol;
};

// Update an existing protocol
export const updateProtocol = async (
	id: number,
	protocolData: Partial<Protocol>
): Promise<Protocol> => {
	const response = await http.put(
		`${API_BASE_URL}/protocols/${id}`,
		protocolData,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data.protocol;
};

// Clone a protocol with new version
export const cloneProtocol = async (
	id: number,
	cloneData: CloneProtocolRequest
): Promise<Protocol> => {
	const response = await http.post(
		`${API_BASE_URL}/protocols/${id}/clone`,
		cloneData,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data.protocol;
};

// Delete a protocol
export const deleteProtocol = async (id: number): Promise<void> => {
	await http.delete(`${API_BASE_URL}/protocols/${id}`);
};


// Get protocols by test type
export const fetchFirstProtocols = async (): Promise<Protocol[]> => {
	const response = await http.get(`${API_BASE_URL}/protocols/first`);

	return response.data;
};


// Get protocols by test type
export const fetchProtocolsByTestType = async (testType: string): Promise<Protocol[]> => {
	const response = await http.get(`${API_BASE_URL}/protocols/test-type/${testType}`);

	return response.data;
};

// Get compatible protocols for test and sample types
export const fetchCompatibleProtocols = async (
	request: CompatibleProtocolsRequest
): Promise<Protocol[]> => {
	const response = await http.post(
		`${API_BASE_URL}/protocols/compatible`,
		request,
		{
			headers: {
				'Content-Type': 'application/json',
			},
		}
	);

	return response.data;
};

// Get protocol statistics
export const fetchProtocolStatistics = async (id: number): Promise<ProtocolStatistics> => {
	const response = await http.get(`${API_BASE_URL}/protocols/${id}/statistics`);

	return response.data;
};

// Activate a protocol
export const activateProtocol = async (id: number): Promise<void> => {
	await http.patch(`${API_BASE_URL}/protocols/${id}/activate`, {});
};

// Deactivate a protocol
export const deactivateProtocol = async (id: number): Promise<void> => {
	await http.patch(`${API_BASE_URL}/protocols/${id}/deactivate`, {});
};